/**
 * ClientsList — manage clients + their pricing model (Admin/HR only).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from '../../utils/toast';
import { Plus, Building2, X, Trash2, Mail, Globe } from 'lucide-react';
import { wsApi, wsBase, assetUrl } from './wsApi';
import { T, PrimaryButton, StatusBadge, ConfirmDialog, selectChevron } from '../../components/em/ui';
import { LogoPicker, ColorSwatchSelect, SkeletonGrid } from './wsUi';
import { CURRENCIES, COUNTRY_CODES, BUDGET_TYPES } from './wsConstants';
import {
  accountNumberMaxLength,
  sanitizeAccountNumberInput,
  validateAccountNumber,
} from '../../utils/accountNumber';

// A client can be overseas (they carry a currency, VAT ID and timezone), so the
// account number follows the IBAN-safe rule: letters and digits, up to 34.
const ACCOUNT_OPTS = { mode: 'iban', required: false, label: 'Account no' };

const EMPTY = {
  company_name: '', industry: '', timezone: '', email: '', contact_name: '',
  status: 'active', notes: '',
  logo: '', color: '#f54b01', account_no: '', vat_id: '', currency: 'INR',
  phone_country: '+91', phone: '', budget: '', hourly_quote: '', budget_type: 'hourly', address: '',
  pricing_type: 'hourly', amount: '',
};

export function ClientModal({ initial, onClose, onSaved }) {
  const editing = !!initial?.id;
  const [form, setForm] = useState({
    ...EMPTY,
    ...(initial || {}),
    // hydrate pricing from latest model if editing
    ...(initial?.pricing?.[0]
      ? {
          pricing_type: initial.pricing[0].pricing_type,
          amount: initial.pricing[0].amount,
          currency: initial.pricing[0].currency,
        }
      : {}),
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.company_name.trim()) return toast.error('Company name is required');
    // Optional, but a supplied value must be storable. The payload carries the
    // cleaned string so it is stored without spaces or hyphens.
    const account = validateAccountNumber(form.account_no, ACCOUNT_OPTS);
    if (!account.ok) return toast.error(account.error);
    const payload = { ...form, account_no: account.value };
    setSaving(true);
    try {
      if (editing) {
        await wsApi.updateClient(initial.id, payload);
        // Append a new pricing row only when what we charge actually changed
        // (the table is append-only history and reports read the latest row).
        // Compare numerically: DECIMAL comes back as "12500.00", so the old
        // string compare never matched and appended a duplicate on every save.
        const prev = initial.pricing?.[0];
        const changed = !prev
          || Number(prev.amount) !== Number(form.amount || 0)
          || prev.pricing_type !== form.pricing_type
          || (prev.currency || 'INR') !== (form.currency || 'INR');
        if (form.amount && changed) {
          await wsApi.addPricing(initial.id, {
            pricing_type: form.pricing_type,
            amount: form.amount,
            currency: form.currency,
          });
        }
      } else {
        await wsApi.createClient(payload);
      }
      toast.success(editing ? 'Client updated' : 'Client created');
      onSaved();
    } catch {
      toast.error('Could not save client');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <div style={modalHead}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>{editing ? 'Edit Client' : 'New Client'}</h3>
          <button onClick={onClose} style={iconBtn}><X size={18} /></button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Field label="Logo">
            <LogoPicker value={form.logo} onChange={(v) => set('logo', v)} color={form.color} label={form.company_name} />
          </Field>
          <Field label="Company Name *">
            <input style={input} value={form.company_name} onChange={(e) => set('company_name', e.target.value)} placeholder="Acme Corp" />
          </Field>
          <Field label="Colour">
            <ColorSwatchSelect value={form.color} onChange={(c) => set('color', c)} />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Industry"><input style={input} value={form.industry || ''} onChange={(e) => set('industry', e.target.value)} /></Field>
            <Field label="Timezone"><input style={input} value={form.timezone || ''} onChange={(e) => set('timezone', e.target.value)} placeholder="Asia/Kolkata" /></Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Contact Name"><input style={input} value={form.contact_name || ''} onChange={(e) => set('contact_name', e.target.value)} /></Field>
            <Field label="Email"><input style={input} value={form.email || ''} onChange={(e) => set('email', e.target.value)} /></Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Account No">
              {/* Separators stripped and hard-capped as you type, so a pasted
                  "GB29 NWBK 6016 1331 9268 19" lands clean. */}
              <input
                style={input}
                value={form.account_no || ''}
                onChange={(e) => set('account_no', sanitizeAccountNumberInput(e.target.value, 'iban'))}
                onBlur={(e) => set('account_no', sanitizeAccountNumberInput(e.target.value, 'iban'))}
                autoComplete="off"
                maxLength={accountNumberMaxLength('iban')}
              />
            </Field>
            <Field label="VAT ID"><input style={input} value={form.vat_id || ''} onChange={(e) => set('vat_id', e.target.value)} /></Field>
          </div>
          <Field label="Phone">
            <div style={{ display: 'flex', gap: 8 }}>
              <select style={{ ...selectInput, width: 110, flexShrink: 0 }} value={form.phone_country || '+91'} onChange={(e) => set('phone_country', e.target.value)}>
                {COUNTRY_CODES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
              </select>
              <input style={input} value={form.phone || ''} onChange={(e) => set('phone', e.target.value)} placeholder="98765 43210" />
            </div>
          </Field>
          <Field label="Address">
            <textarea style={{ ...input, minHeight: 60, resize: 'vertical' }} value={form.address || ''} onChange={(e) => set('address', e.target.value)} />
          </Field>

          {/* ── Internal side: spend ceiling + the cost rate new projects
              inherit when their own fields are left blank. This is NOT what
              the client is billed — that's the section below. ── */}
          <div style={{ background: 'var(--em-bg-head)', borderRadius: 10, padding: 14 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--em-fg2)' }}>Budget cap &amp; cost defaults</div>
            <div style={sectionHint}>
              Internal spend ceiling and cost rate. New projects for this client inherit these when left blank.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
              <Field label="Budget Type">
                <select style={selectInput} value={form.budget_type || 'hourly'} onChange={(e) => set('budget_type', e.target.value)}>
                  {BUDGET_TYPES.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
                </select>
              </Field>
              <Field label="Budget"><input type="number" style={input} value={form.budget} onChange={(e) => set('budget', e.target.value)} placeholder="0" /></Field>
              <Field label="Hourly Quote"><input type="number" style={input} value={form.hourly_quote} onChange={(e) => set('hourly_quote', e.target.value)} placeholder="0" /></Field>
            </div>
            {/* The one and only currency control — it applies to this client's
                budget AND to what they're billed (previously duplicated). */}
            <div style={{ marginTop: 12 }}>
              <Field label="Currency">
                <select style={selectInput} value={form.currency || 'INR'} onChange={(e) => set('currency', e.target.value)}>
                  {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
                </select>
              </Field>
            </div>
          </div>

          {/* ── Revenue side: the ONLY source of Revenue / Profit / Margin.
              Saving a changed rate appends a new pricing row, so past rates
              stay on record and reports keep using the latest. ── */}
          <div style={{ background: 'var(--em-bg-head)', borderRadius: 10, padding: 14 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--em-fg2)' }}>What we charge the client</div>
            <div style={sectionHint}>
              Drives Revenue, Profit and Margin. Changing the rate keeps the previous one in history.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Billing Type">
                <select style={selectInput} value={form.pricing_type || 'hourly'} onChange={(e) => set('pricing_type', e.target.value)}>
                  <option value="hourly">Hourly</option>
                  <option value="fixed">Fixed</option>
                  <option value="retainer">Retainer</option>
                </select>
              </Field>
              <Field label={form.pricing_type === 'hourly' ? 'Rate / hour' : 'Amount'}>
                <input type="number" style={input} value={form.amount} onChange={(e) => set('amount', e.target.value)} placeholder="0" />
              </Field>
            </div>
            <div style={{ ...sectionHint, marginTop: 8, marginBottom: 0 }}>
              {form.pricing_type === 'hourly'
                ? `Billed per billable hour in ${form.currency || 'INR'}.`
                : `Billed as a flat ${form.pricing_type === 'retainer' ? 'retainer' : 'fixed'} amount in ${form.currency || 'INR'}.`}
            </div>
          </div>

          <Field label="Status">
            <select style={selectInput} value={form.status || 'active'} onChange={(e) => set('status', e.target.value)}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </Field>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 22 }}>
          <button onClick={onClose} style={ghostBtn}>Cancel</button>
          <button onClick={save} disabled={saving} style={primaryBtn}>{saving ? 'Saving…' : editing ? 'Save Changes' : 'Create Client'}</button>
        </div>
      </div>
    </div>
  );
}

export default function ClientsList() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    wsApi.listClients()
      .then(setClients)
      .catch(() => toast.error('Could not load clients'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDetail = (id) => navigate(`${wsBase()}/clients/${id}`);

  const [confirming, setConfirming] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const del = async () => {
    if (!confirming) return;
    setDeleting(true);
    try {
      await wsApi.deleteClient(confirming.id);
      setClients((c) => c.filter((x) => x.id !== confirming.id));
      toast.success('Client deleted');
      setConfirming(null);
    } catch {
      toast.error('Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 18 }}>
        <PrimaryButton icon={Plus} onClick={() => setModal({})}>New Client</PrimaryButton>
      </div>

      {loading ? (
        <SkeletonGrid count={6} minWidth={280} height={170} />
      ) : clients.length === 0 ? (
        <div style={{ background: 'var(--em-bg-card)', border: `1px dashed ${T.border}`, borderRadius: 14, padding: 50, textAlign: 'center' }}>
          <Building2 size={42} color={T.fg7} style={{ marginBottom: 12, opacity: 0.6 }} />
          <p style={{ font: `600 15px ${T.font}`, color: T.fg1, margin: '0 0 14px' }}>No clients yet.</p>
          <div style={{ display: 'inline-flex' }}><PrimaryButton icon={Plus} onClick={() => setModal({})}>Add your first client</PrimaryButton></div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
          {clients.map((c) => (
            <div key={c.id} onClick={() => openDetail(c.id)}
              onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 8px 24px -10px rgba(10,10,10,0.18)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = T.shadowSm; e.currentTarget.style.transform = 'none'; }}
              style={{ background: 'var(--em-bg-card)', border: `1px solid ${T.border}`, borderRadius: 14, padding: 18, cursor: 'pointer', boxShadow: T.shadowSm, transition: 'box-shadow 150ms, transform 150ms' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: c.logo ? '#fff' : (c.color || T.purple50), overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {c.logo
                    ? <img src={assetUrl(c.logo)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <Building2 size={19} color="#fff" />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <h3 style={{ margin: 0, font: `700 14px ${T.font}`, color: T.fg1 }}>{c.company_name}</h3>
                  {c.industry && <div style={{ font: `400 11px ${T.font}`, color: T.fg7, marginTop: 2 }}>{c.industry}</div>}
                </div>
                <button onClick={(e) => { e.stopPropagation(); setConfirming(c); }} title="Delete" aria-label={`Delete ${c.company_name}`} style={{ background: 'none', border: 0, cursor: 'pointer', color: T.fg7, display: 'flex', padding: 2 }}><Trash2 size={14} /></button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 14 }}>
                {c.email && <span style={{ display: 'flex', alignItems: 'center', gap: 6, font: `400 12px ${T.font}`, color: T.fg5 }}><Mail size={12} /> {c.email}</span>}
                {c.timezone && <span style={{ display: 'flex', alignItems: 'center', gap: 6, font: `400 12px ${T.font}`, color: T.fg5 }}><Globe size={12} /> {c.timezone}</span>}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, paddingTop: 12, borderTop: `1px solid ${T.divider}` }}>
                <StatusBadge label={c.status === 'active' ? 'Active' : 'Inactive'} />
                <span style={{ flex: 1 }} />
                <span style={{ font: `500 11px ${T.font}`, color: T.fg7 }}>{c.project_count} project{c.project_count !== 1 ? 's' : ''}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <ClientModal initial={modal.id ? modal : null} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />
      )}

      <ConfirmDialog
        open={!!confirming}
        title="Delete client?"
        message={`"${confirming?.company_name}" will be removed. Its projects stay, but are unlinked from any client.`}
        confirmLabel="Delete client"
        danger
        busy={deleting}
        onConfirm={del}
        onCancel={() => !deleting && setConfirming(null)}
      />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--em-fg2)' }}>{label}</span>
      {children}
    </label>
  );
}
const input = { width: '100%', border: '1px solid var(--em-border)', borderRadius: 8, padding: '9px 11px', fontSize: 13.5, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit', background: 'var(--em-bg-card)', color: 'var(--em-fg1)' };
// Same field chrome as `input`, plus a dropdown chevron — for <select>s only
// (a plain <input> shouldn't paint an arrow). appearance:none removes each
// browser's native arrow so this custom one is the only one shown.
// Small caption under a section heading — explains what the block actually
// affects, so the two billing blocks can't be mistaken for each other.
const sectionHint = { fontSize: 11, lineHeight: 1.4, color: 'var(--em-fg7)', margin: '3px 0 10px' };
const selectInput = { ...input, ...selectChevron };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 };
const modal = { background: 'var(--em-bg-card)', borderRadius: 14, padding: '22px 24px', width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.2)' };
const modalHead = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 };
const iconBtn = { background: 'none', border: 'none', cursor: 'pointer', display: 'flex', padding: 4 };
const primaryBtn = { display: 'flex', alignItems: 'center', gap: 7, background: '#f54b01', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600 };
const ghostBtn = { display: 'flex', alignItems: 'center', gap: 7, background: 'var(--em-bg-card)', color: 'var(--em-fg2)', border: '1px solid var(--em-border)', borderRadius: 8, padding: '9px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600 };
